import { Component, OnInit, Input, OnDestroy, EventEmitter, Output, ViewChild, ViewChildren, ElementRef, QueryList, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, FormControl, AbstractControl, Validators } from "@angular/forms";
import { Subscription, Observable, combineLatest } from "rxjs";
import { tap } from "rxjs/operators";
import { parseFileNameFromHeaders } from "../../models/onBaseIntegrationAndMapping.model";
import { ScanDocumentDetailsComponent } from "./scan-document-details/scan-document-details.component";
import { OrganizationStub, Organization, Department } from "../../models/organization.model";
import { Group } from "../../../admin/models/group.model";
import { requireOneOfGroup, RequestForScanning } from "../../models/scan-documents.model";
import { DocumentManagementService } from "../../services/document-management.service";
import { LoaderService } from "../../services/loader.service";
import { GroupService, GroupSearchSpec } from "../../../admin/services/group.service";
import { deepClone } from "../../../builder/models/editor.model";
import { LunaClaimTypes, LunaClaimValues } from "../../../models/user-identity.model";
import { AuthService } from "../../../services/auth.service";
import { OrganizationService } from "../../services/organization.service";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";



@Component({
    selector: 'document-scanning-request',
    templateUrl: './document-scanning-request.component.html',
    styleUrls: ['./document-scanning-request.component.scss'],
    standalone: false
})
export class DocumentScanningRequestComponent implements OnInit, OnDestroy {

    @Input() applicationNumber: string = "";

    @Output() cancelRequest: EventEmitter<string> = new EventEmitter<string>();

    confirmCancelDialog: Dialog = new Dialog("Are you sure you want to cancel this request?", [new DialogButton("Yes", true, () => { this.confirmCancel(); }), new DialogButton("No", false, () => { return; })]);

    get dateInvalid(): boolean {
        let invalid: boolean = false;
        if (this.formGroup) {
            let ctrl = this.formGroup.get("receivedDate");
            invalid = ctrl && ctrl.invalid;
        }
        return invalid;
    }

    @ViewChildren(ScanDocumentDetailsComponent) docForms: QueryList<ScanDocumentDetailsComponent>;
    @ViewChild("downloadLink", { static: true }) download: ElementRef;


    get docFormsValid(): boolean {
        let valid = true;
        if (this.docForms) {
            valid = this.docForms.toArray().every(d => d.apiValid || d.applicationNumberValid || d.organizationNumberValid);
        }
        return valid;
    }

    @Input() enableMultiple: boolean = false;



    formGroup: FormGroup = new FormGroup({});

    get documentArray(): FormArray | null {
        if (this.formGroup) {
            let array = this.formGroup.get("documents");
            if (array) {
                return <FormArray>array;
            }
        }
        return null;
    } 
         
    get groupInvalid(): boolean {
        let invalid: boolean = false;
        if (this.formGroup) {
            let ctrl = this.formGroup.get("group");
            invalid = ctrl && ctrl.invalid;
        }
        return invalid;
    }

    departments: Department[] = [];

    groups: Group[] = [];

    @Input() isAdhoc: boolean = false;
    
    @Input() organization: OrganizationStub;

    get organizationNumber(): string {
        let value: string = "";
        if (this.organization) {
            value = this.organization.id;
        }
        return value;
    }

    get rbdmsNumber(): string {
        let value: string = "";
        if (this.organization) {
            value = this.organization.historicalOwnerNumber;
        }
        return value;
    }

    @Input() paperApplication: boolean = false;
    @Output() requestSubmitted: EventEmitter<RequestForScanning[]> = new EventEmitter<RequestForScanning[]>();


    get requestTime(): Date {
        return new Date();
    }
    selectDepartment = { id: "", displayName: "--Select Group--" } as Department;
    selectGroup = { id: "", displayName: "--Select Group--", permissions: [] } as Group;
    _scanGroup: Group;
    get scanGroup(): Group {
        if (!this._scanGroup) {
            this._scanGroup = deepClone(this.selectGroup);
        }
        return this._scanGroup;
    }
    set scanGroup(value: Group) {
        this._scanGroup = value;
    }

    subscription: Subscription = new Subscription();


    @Output() throwError: EventEmitter<string> = new EventEmitter<string>();


    get totalPages(): number {
        let forms = this.docForms.toArray();
        let pages: number = 0;
        forms.forEach(f => pages += <number>f.formGroup.get("pages").value);
        return pages;
    }

    constructor(private builder: FormBuilder, private docService: DocumentManagementService, public dialogService: DynamicDialogService,
        private groupService: GroupService, public loader: LoaderService, public auth : AuthService, private orgService: OrganizationService) { }

    ngOnInit() {
        this.buildForm();
        let orgObs$ = this.orgService.getOrganization(this.auth.User.organizationId).pipe(tap((org: Organization) => {
            this.departments = org.departments.sort((a, b) => {
                if (a.displayName > b.displayName) return 1;
                if (a.displayName < b.displayName) return -1;
                return 0;
            });
            this.departments.unshift(this.selectGroup);
        })).subscribe(() => { },
            (error) => {
                console.error(error);
                this.throwError.emit(error);
            }
        );
        this.subscription.add(orgObs$);
        let groupObs$ = this.groupService.get({ internal: true, stubs: false } as GroupSearchSpec).pipe(tap((groupData) => {
            let scanEligible: Group[] = (groupData as Group[]).filter(g => g.permissions.find(c => c.type === LunaClaimTypes.Widget && c.value === LunaClaimValues.ViewScanningWidget) != null);
            this.groups = scanEligible;
            this.groups.unshift(this.selectGroup);
            this.setScanningGroup(this.groups);
        })).subscribe(() => {
            this.formGroup.get("requestor").setValue(this.scanGroup.id);
        },
            (error) => { console.error(error); });
        this.subscription.add(groupObs$);
        
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes["applicationNumber"]) {
            let changed = changes["applicationNumber"].previousValue != changes["applicationNumber"].currentValue;
            if (changed && this.documentArray != null) {
                let length = this.documentArray.length;
                this.buildForm(length);
            }
        }
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    addDocument(): void {
        this.documentArray.push(this.buildDocumentForm());
    }

    buildDocumentForm(): FormGroup {
        return this.builder.group({
            adhocRequestId: ["", []],
            documentGroup: [-1, [Validators.required, Validators.min(0)]],
            documentType: [-1, [Validators.required, Validators.min(0)]],
            apiNumber: ["", []],
            apiFound: [false, []],
            applicationNumber: [{ value: this.applicationNumber, disabled: this.applicationNumber !== "" }, []],
            applicationNumberFound: [this.applicationNumber != null, []],
            organizationNumber: [{ value: this.organizationNumber, disabled: this.organizationNumber != "" }, []],
            organizationNumberFound: [this.organization != null, []],
            rbdmsNumber: [{ value: this.rbdmsNumber, disabled: this.rbdmsNumber != "" },],
            rbdmsNumberFound: [ this.organization!= null, []],
            confidential: [false, []],
            legalReview: [false, []],
            pages: [0, [Validators.min(1), Validators.required]]
        }, { validators: [requireOneOfGroup(["apiFound", "applicationNumberFound", "organizationNumberFound", "rbdmsNumberFound"]), requireOneOfGroup(["applicationNumber", "organizationNumber", "apiNumber", "rbdmsNumber"])] }); 
    }

    buildForm(documentCount: number = 1): void {
        const array = [];
        for (let i = 0; i < documentCount; i++) {
            array.push(this.buildDocumentForm());
        }
       
        this.formGroup = this.builder.group({
            receivedDate: ["", [Validators.required]],
            group: ["", [Validators.required]],
            requestor: [this.scanGroup.id, [Validators.required]],
            documents: this.builder.array(array),
            instructions: ["", []]
        });
    }

    cancel(): void {
        this.confirmCancelDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.confirmCancelDialog);
    }

    confirmCancel(): void {
        this.formGroup.reset();
        this.cancelRequest.emit("cancelled");
    }

    private generateScanRequests(): RequestForScanning[] {
        const dateReceived = this.formGroup.get("receivedDate").value;
        const instructions = this.formGroup.get("instructions").value;
        const requests: RequestForScanning[] = [];
        this.docForms.forEach(f => {           
            let request = {
                adhocRequestId: <string>f.formGroup.get("adhocRequestId").value,
                apiNumber: <string>f.formGroup.get("apiNumber").value,
                applicationNumber: <string>f.formGroup.get("applicationNumber").value,
                confidential: <boolean>f.formGroup.get("confidential").value,
                dateDocumentReceived: dateReceived,
                documentType: <string>f.selectedDocumentType.name,
                groupRequesting: <string>this.formGroup.get("group").value,
                instructions: instructions,
                isAdhoc: this.isAdhoc,
                legalReviewRequired: <boolean>f.formGroup.get("legalReview").value,
                pages: <number>f.formGroup.get("pages").value,
                organization: (f.organization != null && f.organization.id !== "new") ? f.organization.id : "",
                organizationName: (f.organization != null && f.organization.id !== "new") ? f.organization.name : "",
                documentGroup: <string>f.selectedDocumentGroup.name,
                rbdmsNumber: (f.organization != null && f.organization.historicalOwnerNumber != null) ? f.organization.historicalOwnerNumber : "",
                requestor: <string>this.formGroup.get("requestor").value,
                timeOfRequest: this.requestTime.toLocaleTimeString()
            } as RequestForScanning;
            requests.push(request);
        });

        return requests;
    }

    print(): void {
        const requests = this.generateScanRequests();
        let obs$: Observable<any>[] = [];
        requests.forEach(r => obs$.push(this.docService.generateScanningCover(r)));
        this.loader.toggleLoader(true);
        let docObs$ = combineLatest(...obs$).pipe().subscribe(
            (data) => {
                this.loader.toggleLoader(false);
                this.renderBlobs(data);
            }, (error) => {
                console.error(error);
                this.loader.toggleLoader(false);
                this.throwError.emit(error);
            });
        this.subscription.add(docObs$);
    }

    receivedDateChanged(event: Date): void {
        this.formGroup.get("receivedDate").patchValue(event);
        this.formGroup.updateValueAndValidity();
    }

    renderBlobs(data: any[]): void {
        let fileNames: string[] = [];
        for (let u = 0; u < data.length; u++) {
            const blob = new Blob([data[u].body], { type: "application/pdf" });
            let fileName = parseFileNameFromHeaders(data[u].headers.get("content-disposition"));
            if (this.isAdhoc) {
                let nameParts = fileName.split('.');
                if (nameParts.length > 0) {
                    fileNames.push(nameParts[0]);
                }
            }
            let url = window.URL.createObjectURL(blob);
            const link = this.download.nativeElement;
            link.href = url;
            link.download = fileName;
            link.click();
        }
        let index = 0;
        if (this.isAdhoc) {
            this.docForms.forEach(f => {
                f.formGroup.get('adhocRequestId').setValue(fileNames[index]);
                index++;
            });
        }
    }


    setScanningGroup(groupList: Group[]): void {
        let scanEligible = groupList.filter(g => g.permissions.find(c => c.type === LunaClaimTypes.Widget && c.value === LunaClaimValues.ViewScanningWidget) != null);
        if (scanEligible) {
            if (scanEligible.length === 1) {
                this.scanGroup = scanEligible[0];
            } else {
                this.scanGroup = scanEligible.find(s => s.displayName.toLocaleLowerCase().indexOf("scan") !== -1);
            }
        }
    }

    submit(): void {
        const requests: RequestForScanning[] = this.generateScanRequests();
        this.requestSubmitted.emit(requests);
    }
}
